import type { BillResponse, PurchaseOrderResponse } from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

const CHIP =
  'inline-flex items-center whitespace-nowrap rounded-full border border-transparent px-2.5 py-0.5 text-xs font-semibold';

/**
 * PUR-01 §7 — the six-status ladder plus cancelled. BR-PUR-01-06 and the §15
 * proposed default applied: the prototype's one-off violet **Received** is
 * repainted GREEN and **Closed** faded green, keeping the palette at five.
 */
const PO_CLASSES: Record<PurchaseOrderResponse['status'], string> = {
  draft: 'bg-muted text-muted-foreground',
  approved: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  sent: 'bg-blue-200 text-blue-900 dark:bg-blue-900 dark:text-blue-200',
  partially_received: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  received: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  /*
   * These two are faded on the FILL rather than on the chip. `opacity-70` /
   * `opacity-80` composited the ink toward the surface along with its own
   * background, so the two closed on each other: Cancelled 3.69:1 and Closed
   * 3.48:1 against a bar of 4.5.
   *
   * Closed faded TWICE — a 60% fill inside an 80% element — so a browser
   * painted its fill at 0.6 × 0.8 = 48%. `/48` reproduces that pixel exactly
   * rather than rounding the look to a tidier number, which is the whole
   * claim being made here: the chip is unchanged and only the ink moved.
   * 5.05:1 and 5.59:1 after, with no colour value touched.
   */
  closed: 'bg-emerald-100/48 text-emerald-700 dark:bg-emerald-950/48 dark:text-emerald-400',
  cancelled: 'bg-red-100/70 text-red-700 dark:bg-red-950/70 dark:text-red-300',
};

export function PoStatusChip({
  status,
  className,
}: {
  status: PurchaseOrderResponse['status'];
  className?: string;
}) {
  const { t } = useTranslation();
  return <span className={cn(CHIP, PO_CLASSES[status], className)}>{t(`po.status.${status}`)}</span>;
}

/**
 * §7.5's verdict chip — recomputed by the server on every read and shown
 * WHATEVER the stored status says (BR-PUR-03-14's "forced sixth").
 *
 * ERR-PUR-03-01 is this chip from the user's side, and its §14 row describes
 * the behaviour as correct rather than broken: "Opened a bill whose amounts
 * disagree | (the verdict reads 'Variance found' and the chip is forced to
 * Variance) | Fix the bill, or ask a manager to override." Both halves are
 * here — `bills.matchStatus.variance` is that exact verdict, and "forced"
 * is BR-PUR-03-14 above. The override half is BR-PUR-03-16's
 * purchasing.approve acceptance, and the refusal in the meantime is
 * BR-PUR-03-01 / ERR-PUR-03-02's `varianceBlocked`.
 */
const MATCH_CLASSES: Record<BillResponse['matchStatus'], string> = {
  matched: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  variance: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  no_order: 'bg-muted text-muted-foreground',
};

export function MatchChip({
  status,
  className,
}: {
  status: BillResponse['matchStatus'];
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <span className={cn(CHIP, MATCH_CLASSES[status], className)}>
      {t(`bills.matchStatus.${status}`)}
    </span>
  );
}
